import { useEffect, useRef, useState } from "react";
import { log } from "@/lib/log";
import { Link, useSearchParams } from "react-router-dom";
import { auth as authApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, MailCheck } from "lucide-react";
import useAppReady from "@/lib/useAppReady";

export default function VerifyEmail() {
  useAppReady();
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ status: "loading", message: "" });
  const ranRef = useRef(false);

  // Mount-only — `token` is from URL query params and is stable for this
  // route render; `ranRef` guards against React 19 StrictMode double-effect.
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) {
      setState({ status: "error", message: "Verification link is missing the token." });
      return;
    }
    (async () => {
      try {
        const r = await authApi.verifyEmail(token);
        setState({ status: "ok", message: r?.message || "Email verified." });
        // Refresh user so the banner disappears next render.
        try { await refresh(); } catch (err) { log.warn("[VerifyEmail] user refresh after verification failed:", err); }
      } catch (e) {
        setState({ status: "error", message: e?.response?.data?.detail || "Could not verify email." });
      }
    })();
  }, [token, refresh]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-soft-md p-8 sm:p-10 max-w-md w-full border border-plum/5 text-center" data-testid="verify-email-root">
        {state.status === "loading" && (
          <>
            <div className="w-14 h-14 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-4">
              <MailCheck className="w-7 h-7 text-coral" />
            </div>
            <h1 className="font-heading text-2xl mb-2">Verifying your email…</h1>
            <p className="text-inkmid text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state.status === "ok" && (
          <>
            <div className="w-14 h-14 rounded-full bg-mint/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-mint-dark" />
            </div>
            <h1 className="font-heading text-2xl mb-2" data-testid="verify-success">You&apos;re verified 🌿</h1>
            <p className="text-inkmid text-sm mb-6">{state.message}</p>
            <Link to={user ? "/activities" : "/login"}>
              <Button className="rounded-full bg-coral hover:bg-coral-dark text-white font-bold px-6">
                {user ? "Continue to Kiddo+" : "Sign in"}
              </Button>
            </Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <div className="w-14 h-14 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-7 h-7 text-coral" />
            </div>
            <h1 className="font-heading text-2xl mb-2" data-testid="verify-error">Something went wrong</h1>
            <p className="text-inkmid text-sm mb-6">{state.message}</p>
            <Link to={user ? "/edit-profile" : "/login"}>
              <Button variant="outline" className="rounded-full border-plum/10 px-6">
                {user ? "Try again from Settings" : "Back to sign in"}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
